import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../services/api';
import { 
  Buildings, Users, Receipt, CurrencyEur, ChartBar, ChartLineUp,
  SignOut, List, X, Key, Folders, FileText, ChatCircleDots, ShieldCheck, Crown, House, Gear, Warning, Gauge, FileArrowUp
} from '@phosphor-icons/react';
import SystemNoticeBanner from './SystemNoticeBanner';
import LegalFooter from './LegalFooter';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: ChartBar, label: 'Dashboard' },
    { to: '/properties', icon: Buildings, label: 'Immobilien' },
    { to: '/tenants', icon: Users, label: 'Mieter' },
    { to: '/mieteinnahmen', icon: House, label: 'Mieteinnahmen' },
    { to: '/receipts', icon: Receipt, label: 'Belege' },
    { to: '/kostenstellen', icon: Folders, label: 'Kostenstellen' },
    { to: '/transactions', icon: CurrencyEur, label: 'Transaktionen' },
    { to: '/jahresabrechnung', icon: FileText, label: 'Abrechnung' },
    { to: '/zaehler', icon: Gauge, label: 'Zähler' },
    { to: '/hv-abrechnung', icon: FileArrowUp, label: 'HV-Import' },
    { to: '/gewinn-verlust', icon: ChartLineUp, label: 'Gewinn / Verlust' },
    { to: '/messages', icon: ChatCircleDots, label: 'Nachrichten' },
    { to: '/mahnungen', icon: Warning, label: 'Mahnungen' },
    { to: '/ki-protokoll', icon: ShieldCheck, label: 'KI-Protokoll' },
    { to: '/settings', icon: Gear, label: 'Einstellungen' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="app-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Buildings weight="bold" className="w-7 h-7" />
          <span className="font-heading text-lg font-black tracking-tight">IMMO VERWALTER</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-white hover:bg-slate-800 rounded-lg"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X weight="bold" className="w-6 h-6" /> : <List weight="bold" className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen z-50 bg-slate-900 text-white flex flex-col transition-transform duration-300
          w-72 lg:w-64
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        data-testid="sidebar"
      >
        {/* Logo - Hidden on mobile (shown in header) */}
        <div className="hidden lg:flex p-6 border-b border-slate-700 items-center gap-3">
          <Buildings weight="bold" className="w-8 h-8 text-white flex-shrink-0" />
          <span className="font-heading text-lg font-black tracking-tight">IMMO VERWALTER</span>
        </div>

        {/* Mobile Close Button */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-700">
          <span className="font-heading text-lg font-bold">Menü</span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 text-slate-400 hover:text-white"
          >
            <X weight="bold" className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {user?.role === 'super_admin' && (
            <NavLink
              to="/super-admin"
              className="flex items-center gap-3 px-4 py-3.5 bg-amber-400/10 border border-amber-400/30 text-amber-300 hover:bg-amber-400/20 transition-colors rounded-lg mb-3"
              data-testid="nav-super-admin"
            >
              <Crown weight="fill" className="w-5 h-5 flex-shrink-0 text-amber-400" />
              <span className="font-medium">Super-Admin</span>
            </NavLink>
          )}
          {user?.mandant?.name && (
            <div className="px-4 py-2 mb-2 text-xs text-slate-500 border-b border-slate-800">
              <p className="uppercase tracking-widest font-bold">Mandant</p>
              <p className="text-slate-300 font-medium truncate">{user.mandant.name}</p>
            </div>
          )}
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => 
                `flex items-center gap-3 px-4 py-3.5 transition-colors rounded-lg ${
                  isActive 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon weight="bold" className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-slate-700 flex items-center justify-center text-white font-bold flex-shrink-0 rounded-lg">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="overflow-hidden flex-1">
              <p className="font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => { setShowPasswordModal(true); setSidebarOpen(false); }}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors rounded-lg"
            data-testid="change-password-button"
          >
            <Key weight="bold" className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">Passwort ändern</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors rounded-lg"
            data-testid="logout-button"
          >
            <SignOut weight="bold" className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen flex flex-col min-w-0">
        <div className="sticky top-0 z-30 lg:top-0 mt-16 lg:mt-0">
          <SystemNoticeBanner />
        </div>
        <div className="p-4 sm:p-6 lg:p-8 flex-1 min-w-0 overflow-x-hidden">
          {children}
        </div>
        <LegalFooter />
      </main>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}

function PasswordChangeModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Die neuen Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      setError('Das neue Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setLoading(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e).join(' '));
      } else {
        setError('Fehler beim Ändern des Passworts');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="password-modal">
      <div className="bg-white w-full max-w-md">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <Key weight="bold" className="w-5 h-5 text-slate-600" />
            </div>
            <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900">Passwort ändern</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-4 sm:p-6">
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 text-center">
              <p className="font-medium">Passwort erfolgreich geändert!</p>
              <p className="text-sm mt-1">Sie können sich jetzt mit Ihrem neuen Passwort anmelden.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="password-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Aktuelles Passwort
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                placeholder="••••••••"
                required
                data-testid="current-password-input"
              />
            </div>

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Neues Passwort
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Neues Passwort bestätigen
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 focus:ring-0 outline-none"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-slate-900 text-white py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50"
                data-testid="save-password-button"
              >
                {loading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
